import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { auth, db } from "../../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
    doc, getDoc, setDoc, updateDoc, collection, getDocs, deleteDoc, query, where,
} from "firebase/firestore";
import Pagination from "../helpers/Pagination";
import { showSuccessToast, showErrorToast } from "../helpers/Toast";
import { Toaster } from "react-hot-toast";

import { FaUpload, FaUserEdit, FaUserSlash, FaFileExcel, FaInfoCircle } from "react-icons/fa";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// MOVED OUTSIDE: Defining this outside the component fixes the useEffect dependency warning
const sectionList = ["A", "B", "C", "D", "E", "F"];

const UploadStudents = () => {
    const UPLOAD_DELAY_MS = 500;

    const [activeTab, setActiveTab] = useState('upload');
    const [file, setFile] = useState(null);

    // State for filtering/pagination
    const semList = [1, 2, 3, 4, 5, 6, 7, 8];
    const [availableSections, setAvailableSections] = useState([]);
    const STUDENTS_PER_PAGE = 10;

    // Update Tab State
    const [updateSelectedSem, setUpdateSelectedSem] = useState("");
    const [updateSelectedSection, setUpdateSelectedSection] = useState("");
    const [updateSemStudents, setUpdateSemStudents] = useState([]);
    const [updateCheckedStudents, setUpdateCheckedStudents] = useState([]);
    const [updateSelectAll, setUpdateSelectAll] = useState(false);
    const [newSem, setNewSem] = useState("");
    const [updateCurrentPage, setUpdateCurrentPage] = useState(1);

    // Delete Tab State
    const [deleteSelectedSem, setDeleteSelectedSem] = useState("");
    const [deleteSelectedSection, setDeleteSelectedSection] = useState("");
    const [deleteSemStudents, setDeleteSemStudents] = useState([]);
    const [deleteCheckedStudents, setDeleteCheckedStudents] = useState([]);
    const [deleteSelectAll, setDeleteSelectAll] = useState(false);
    const [deletionConfirmed, setDeletionConfirmed] = useState(false);
    const [deleteCurrentPage, setDeleteCurrentPage] = useState(1);

    // --- FETCH DYNAMIC SECTIONS ON MOUNT ---
    useEffect(() => {
        const fetchSections = async () => {
            try {
                const sectionSnapshot = await getDocs(collection(db, "sections"));
                const dbSections = sectionSnapshot.docs.map((doc) => doc.id).sort();
                // Fallback to A-F if the database collection is empty
                setAvailableSections(dbSections.length > 0 ? dbSections : sectionList);
            } catch (error) {
                console.error("Error fetching sections:", error);
                setAvailableSections(sectionList);
            }
        };
        fetchSections();
    }, []); // Dependency array is now correct because sectionList is external and stable

    // --- Fetch students for UPDATE tab ---
    useEffect(() => {
        const fetchStudents = async () => {
            if (!updateSelectedSem) {
                setUpdateSemStudents([]);
                return;
            }
            try {
                let q = query(collection(db, "students"), where("semester", "==", Number(updateSelectedSem)));
                if (updateSelectedSection) {
                    q = query(q, where("section", "==", updateSelectedSection));
                }
                const snapshot = await getDocs(q);
                const studentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUpdateSemStudents(studentsList);
            } catch (err) {
                showErrorToast("Failed to fetch students: " + err.message);
            }
        };
        fetchStudents();
    }, [updateSelectedSem, updateSelectedSection]);

    // --- Fetch students for DELETE tab ---
    useEffect(() => {
        const fetchStudents = async () => {
            if (!deleteSelectedSem) {
                setDeleteSemStudents([]);
                return;
            }
            try {
                let q = query(collection(db, "students"), where("semester", "==", Number(deleteSelectedSem)));
                if (deleteSelectedSection) {
                    q = query(q, where("section", "==", deleteSelectedSection));
                }
                const snapshot = await getDocs(q);
                const studentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setDeleteSemStudents(studentsList);
            } catch (err) {
                showErrorToast("Failed to fetch students: " + err.message);
            }
        };
        fetchStudents();
    }, [deleteSelectedSem, deleteSelectedSection]);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return showErrorToast("Please select an Excel file.");

        const validExtensions = ['xlsx', 'xls'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
            return showErrorToast("Invalid file type. Please upload a valid .xlsx or .xls file.");
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            let successCount = 0;
            const failed = [];

            try {
                const data = e.target.result;
                let workbook;

                try {
                    workbook = XLSX.read(data, { type: "array" });
                } catch (readError) {
                    throw new Error("Invalid file content. The file could not be read as Excel.");
                }

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                if (jsonData.length === 0) {
                    return showErrorToast("The Excel file is empty.");
                }

                const requiredFields = [
                    'usn', 'name', 'email', 'semester', 'section',
                    'department', 'gradYear', 'parentEmail', 'parentNo', 'phone', 'role'
                ];

                for (const [index, row] of jsonData.entries()) {
                    const admissionId = row["Admission ID"]?.toString().trim();
                    const email = row.email?.toString().trim();

                    if (!admissionId || !email) {
                        failed.push({ admissionId: admissionId || `Row ${index + 2}`, error: "Missing required 'Admission ID' or 'email' field." });
                        continue;
                    }

                    try {
                        const studentDocData = {};
                        let missingField = false;

                        for (const field of requiredFields) {
                            const value = row[field];
                            if (value === undefined || value === null || value === "") {
                                missingField = true;
                                break;
                            }

                            if (['gradYear', 'parentNo', 'phone', 'semester'].includes(field)) {
                                studentDocData[field] = Number(value);
                                if (isNaN(studentDocData[field])) {
                                    throw new Error(`Invalid number for field: ${field}`);
                                }
                            } else {
                                studentDocData[field] = value.toString().trim();
                            }
                        }

                        if (missingField) {
                            failed.push({ admissionId: admissionId, error: "Missing one or more required fields." });
                            continue;
                        }

                        const studentRef = doc(db, "students", admissionId);
                        const studentSnap = await getDoc(studentRef);

                        if (studentSnap.exists()) {
                            await updateDoc(studentRef, studentDocData);
                        } else {
                            await sleep(UPLOAD_DELAY_MS);
                            const userCredential = await createUserWithEmailAndPassword(auth, email, "college@123");
                            const user = userCredential.user;
                            await setDoc(studentRef, {
                                ...studentDocData,
                                uid: user.uid,
                            });
                        }
                        successCount++;

                    } catch (err) {
                        let errorMsg = err.message;
                        if (err.code === "auth/email-already-in-use") {
                            errorMsg = "Email already in use by another account.";
                        }
                        failed.push({ admissionId: admissionId, email: email, error: errorMsg });
                    }
                }

                if (failed.length > 0) {
                    console.error("Upload Errors:", failed);
                    showErrorToast("Criteria not met. Recheck file and reupload.");
                } else {
                    showSuccessToast(`Upload successful! ${successCount} students processed.`);
                }

            } catch (err) {
                showErrorToast(err.message);
            }
        };

        reader.onerror = () => {
            showErrorToast("Failed to read the file.");
        };

        reader.readAsArrayBuffer(file);
    };

    const handleUpdateSemester = async () => {
        if (updateCheckedStudents.length === 0 || newSem === "") return showErrorToast("Please select students and a new semester.");
        if (Number(newSem) > 8 || Number(newSem) < 1) return showErrorToast("Semester must be between 1 and 8.");

        try {
            const batch = [];
            updateCheckedStudents.forEach(id => {
                const docRef = doc(db, "students", id);
                batch.push(updateDoc(docRef, { semester: Number(newSem) }));
            });
            await Promise.all(batch);

            showSuccessToast(`Updated ${updateCheckedStudents.length} students to semester ${newSem}`);

            let q = query(collection(db, "students"), where("semester", "==", Number(updateSelectedSem)));
            if (updateSelectedSection) {
                q = query(q, where("section", "==", updateSelectedSection));
            }
            const snapshot = await getDocs(q);
            const studentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUpdateSemStudents(studentsList);

            setUpdateCheckedStudents([]);
            setUpdateSelectAll(false);
            setNewSem("");

        } catch (err) {
            showErrorToast("Update failed: " + err.message);
        }
    };

    const deleteStudentMarks = async (usn) => {
        const marksQuery = query(collection(db, "marks"), where("rollNo", "==", usn));
        const marksSnap = await getDocs(marksQuery);
        const deletePromises = [];
        marksSnap.forEach((doc) => {
            deletePromises.push(deleteDoc(doc.ref));
        });
        await Promise.all(deletePromises);
    };

    const handleDeleteStudents = async () => {
        if (!deletionConfirmed) return showErrorToast("Please confirm deletion by checking the box.");
        if (deleteCheckedStudents.length === 0) return showErrorToast("Please select at least one student to delete.");

        if (!window.confirm(`Are you sure you want to permanently delete ${deleteCheckedStudents.length} students? This cannot be undone.`)) return;

        try {
            const deletePromises = [];
            deleteCheckedStudents.forEach(student => {
                deletePromises.push(deleteDoc(doc(db, "students", student.id)));
                deletePromises.push(deleteStudentMarks(student.usn));
            });

            await Promise.all(deletePromises);

            showSuccessToast(`Successfully deleted ${deleteCheckedStudents.length} students and their data.`);

            let q = query(collection(db, "students"), where("semester", "==", Number(deleteSelectedSem)));
            if (deleteSelectedSection) {
                q = query(q, where("section", "==", deleteSelectedSection));
            }
            const snapshot = await getDocs(q);
            const studentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDeleteSemStudents(studentsList);

            setDeleteCheckedStudents([]);
            setDeleteSelectAll(false);
            setDeletionConfirmed(false);
        } catch (err) {
            showErrorToast(`Deletion failed: ${err.message}`);
        }
    };

    //pagination and checkbox handlers
    const toggleUpdateStudentCheck = (id) => {
        setUpdateCheckedStudents(prev =>
            prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]
        );
    };

    const toggleUpdateSelectAll = (checked) => {
        setUpdateSelectAll(checked);
        setUpdateCheckedStudents(checked ? updateSemStudents.map(s => s.id) : []);
    };

    const toggleDeleteStudentCheck = (student) => {
        setDeleteCheckedStudents(prev =>
            prev.some(s => s.id === student.id)
                ? prev.filter(s => s.id !== student.id)
                : [...prev, { id: student.id, usn: student.usn }]
        );
    };

    const toggleDeleteSelectAll = (checked) => {
        setDeleteSelectAll(checked);
        setDeleteCheckedStudents(checked ? deleteSemStudents.map(s => ({ id: s.id, usn: s.usn })) : []);
    };

    const totalUpdatePages = Math.ceil(updateSemStudents.length / STUDENTS_PER_PAGE);
    const paginatedUpdateStudents = updateSemStudents.slice((updateCurrentPage - 1) * STUDENTS_PER_PAGE, updateCurrentPage * STUDENTS_PER_PAGE);
    const totalDeletePages = Math.ceil(deleteSemStudents.length / STUDENTS_PER_PAGE);
    const paginatedDeleteStudents = deleteSemStudents.slice((deleteCurrentPage - 1) * STUDENTS_PER_PAGE, deleteCurrentPage * STUDENTS_PER_PAGE);


    const TabButton = ({ tabName, label, icon: Icon }) => (
        <button
            onClick={() => { setActiveTab(tabName); }}
            className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm rounded-t-lg border-b-4 transition-colors ${activeTab === tabName
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
        >
            <Icon /> {label}
        </button>
    );

    return (
        <div className="space-y-8">
            <Toaster position="bottom-right" />

            <div className="flex border-b border-gray-200">
                <TabButton tabName="upload" label="Upload New Students" icon={FaUpload} />
                <TabButton tabName="update" label="Update Semesters" icon={FaUserEdit} />
                <TabButton tabName="delete" label="Delete Students" icon={FaUserSlash} />
            </div>

            {/* --- UPLOAD TAB --- */}
            {activeTab === 'upload' && (
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3 justify-center">Upload Students via Excel</h2>

                    <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-4 mb-6 rounded-r-lg text-sm">
                        <div className="flex items-start"><FaInfoCircle className="h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                            <div>
                                <p className="font-bold ">Instructions:</p>
                                <p>Upload an `.xlsx` or `.xls` file with the following columns:</p>
                                <ul className="list-disc list-inside text-xs font-normal pl-2">
                                    <li>Admission ID (This will be the Document ID)</li>
                                    <li>usn</li>
                                    <li>name</li>
                                    <li>email</li>
                                    <li>semester</li>
                                    <li>section</li>
                                    <li>department</li>
                                    <li>gradYear</li>
                                    <li>parentEmail</li>
                                    <li>parentNo</li>
                                    <li>phone</li>
                                    <li>role</li>
                                </ul>
                                <p className="mt-2">The system will create new students or update existing ones based on the **Admission ID**.</p>
                            </div>
                        </div>
                    </div>

                    <input type="file" id="student-file-upload" accept=".xlsx, .xls" onChange={handleFileChange} className="hidden" />
                    <label htmlFor="student-file-upload" className="w-full flex flex-col items-center px-4 py-6 bg-white text-blue-600 rounded-lg shadow-sm border-2 border-dashed border-gray-300 cursor-pointer hover:bg-blue-50 hover:border-blue-500 transition">
                        <FaFileExcel className="w-10 h-10 mb-3" />
                        <span className="text-base font-semibold">{file ? `Selected: ${file.name}` : "Click to select an Excel file"}</span>
                    </label>
                    <button onClick={handleUpload} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition">Upload File</button>
                </div>
            )}

            {/* --- UPDATE TAB --- */}
            {activeTab === 'update' && (
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3 justify-center">Update Student Semesters</h2>
                    <div className="flex flex-col md:flex-row gap-4 mb-6 items-center bg-gray-50 p-4 rounded-lg">
                        <select value={updateSelectedSem} onChange={(e) => setUpdateSelectedSem(e.target.value)} className="w-full md:w-auto p-2 border border-gray-300 rounded-md">
                            <option value="">Filter by Semester</option>
                            {semList.map((sem) => (<option key={sem} value={sem}>{sem}</option>))}
                        </select>
                        <select value={updateSelectedSection} onChange={(e) => setUpdateSelectedSection(e.target.value)} className="w-full md:w-auto p-2 border border-gray-300 rounded-md">
                            <option value="">Filter by Section</option>
                            <option value="">All Sections</option>
                            {availableSections.map((section) => (<option key={section} value={section}>{section}</option>))}
                        </select>
                        <div className="flex-grow"></div>
                        <input type="number" value={newSem} onChange={(e) => setNewSem(e.target.value)} placeholder="New Semester" className="w-full md:w-48 p-2 border border-gray-300 rounded-md" min={1} max={8} />
                        <button className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold" onClick={handleUpdateSemester}>Update Selected</button>
                    </div>
                    {updateSemStudents.length > 0 && (
                        <div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-700">
                                    <thead className="text-xs text-white uppercase bg-blue-600">
                                        <tr>
                                            <th scope="col" className="p-4"><input type="checkbox" checked={updateSelectAll} onChange={(e) => toggleUpdateSelectAll(e.target.checked)} /></th>
                                            <th scope="col" className="px-6 py-3">Admission ID</th>
                                            <th scope="col" className="px-6 py-3">USN</th>
                                            <th scope="col" className="px-6 py-3">Name</th>
                                            <th scope="col" className="px-6 py-3">Email</th>
                                            <th scope="col" className="px-6 py-3">Section</th>
                                            <th scope="col" className="px-6 py-3">Semester</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedUpdateStudents.map((s) => (
                                            <tr key={s.id} className="bg-white border-b hover:bg-gray-50">
                                                <td className="w-4 p-4"><input type="checkbox" checked={updateCheckedStudents.includes(s.id)} onChange={() => toggleUpdateStudentCheck(s.id)} /></td>
                                                <td className="px-6 py-4 font-medium">{s.id}</td>
                                                <td className="px-6 py-4">{s.usn}</td>
                                                <td className="px-6 py-4">{s.name}</td>
                                                <td className="px-6 py-4">{s.email}</td>
                                                <td className="px-6 py-4">{s.section}</td>
                                                <td className="px-6 py-4">{s.semester}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalUpdatePages > 1 && (<div className="flex justify-center mt-4"><Pagination currentPage={updateCurrentPage} totalPages={totalUpdatePages} onPageChange={setUpdateCurrentPage} /></div>)}
                        </div>
                    )}
                    {updateSemStudents.length === 0 && updateSelectedSem && (
                        <p className="text-center text-gray-500 py-10">No students found for the selected filter.</p>
                    )}
                </div>
            )}

            {/* --- DELETE TAB --- */}
            {activeTab === 'delete' && (
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-red-700 flex items-center gap-3 justify-center">Delete Students</h2>
                    <div className="flex flex-col md:flex-row gap-4 mb-6 items-center bg-red-50 p-4 rounded-lg">
                        <select value={deleteSelectedSem} onChange={(e) => setDeleteSelectedSem(e.target.value)} className="w-full md:w-auto p-2 border border-gray-300 rounded-md">
                            <option value="">Filter by Semester</option>
                            {semList.map((sem) => (<option key={sem} value={sem}>{sem}</option>))}
                        </select>
                        <select value={deleteSelectedSection} onChange={(e) => setDeleteSelectedSection(e.target.value)} className="w-full md:w-auto p-2 border border-gray-300 rounded-md">
                            <option value="">Filter by Section</option>
                            <option value="">All Sections</option>
                            {availableSections.map((section) => (<option key={section} value={section}>{section}</option>))}
                        </select>
                    </div>

                    {deleteSemStudents.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-700">
                                <thead className="text-xs text-white uppercase bg-red-600">
                                    <tr>
                                        <th scope="col" className="p-4"><input type="checkbox" checked={deleteSelectAll} onChange={(e) => toggleDeleteSelectAll(e.target.checked)} /></th>
                                        <th scope="col" className="px-6 py-3">Admission ID</th>
                                        <th scope="col" className="px-6 py-3">USN</th>
                                        <th scope="col" className="px-6 py-3">Name</th>
                                        <th scope="col" className="px-6 py-3">Email</th>
                                        <th scope="col" className="px-6 py-3">Section</th>
                                        <th scope="col" className="px-6 py-3">Semester</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedDeleteStudents.map((s) => (
                                        <tr key={s.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="w-4 p-4">
                                                <input
                                                    type="checkbox"
                                                    checked={deleteCheckedStudents.some(student => student.id === s.id)}
                                                    onChange={() => toggleDeleteStudentCheck(s)}
                                                />
                                            </td>
                                            <td className="px-6 py-4 font-medium">{s.id}</td>
                                            <td className="px-6 py-4">{s.usn}</td>
                                            <td className="px-6 py-4">{s.name}</td>
                                            <td className="px-6 py-4">{s.email}</td>
                                            <td className="px-6 py-4">{s.section}</td>
                                            <td className="px-6 py-4">{s.semester}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {deleteSemStudents.length === 0 && deleteSelectedSem && (
                        <p className="text-center text-gray-500 py-10">No students found for the selected filter.</p>
                    )}

                    {totalDeletePages > 1 && (<div className="flex justify-center mt-4"><Pagination currentPage={deleteCurrentPage} totalPages={totalDeletePages} onPageChange={setDeleteCurrentPage} /></div>)}

                    <div className="mt-6 p-4 bg-red-100 border-l-4 border-red-500 rounded-r-lg flex flex-col md:flex-row items-center gap-4">
                        <label className="flex-grow font-semibold text-red-800">
                            <input type="checkbox" checked={deletionConfirmed} onChange={(e) => setDeletionConfirmed(e.target.checked)} className="mr-2 h-4 w-4 accent-red-600" />
                            I understand this will permanently delete the students and all their related data.
                        </label>
                        <button
                            disabled={!deletionConfirmed || deleteCheckedStudents.length === 0}
                            className={`w-full md:w-auto px-6 py-2 rounded-md font-bold text-white transition flex items-center justify-center gap-2 ${(deletionConfirmed && deleteCheckedStudents.length > 0)
                                    ? "bg-red-600 hover:bg-red-700"
                                    : "bg-gray-400 cursor-not-allowed"
                                }`}
                            onClick={handleDeleteStudents}
                        >
                            <FaUserSlash />
                            Delete Selected ({deleteCheckedStudents.length})
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadStudents;